<template>
	<div v-if="appear" class="duplicates">
		<div class="duplicates-header">
			<sym-icon name="duplicates"></sym-icon>
			{{ env.msg('duplicates.title') }}
		</div>
		<div class="duplicates-items">
			<duplicate-party v-for="dup in duplicates" :data="data" @check="sendQueryForDuplicates" :duplicate="dup"></duplicate-party>
		</div>
	</div>
</template>

<script setup lang="ts">
import {computed, defineProps, nextTick, onMounted, ref, watch} from "vue";
import {injectEnv, injectEventBus} from "../Environment.js";
import type {DuplicityQuery, DuplicityReponse, PartyDuplicate} from "@t/customer/DuplicityChecker";
import type {PartyGraph} from "@t/customer/Customer";
import DuplicateParty from "./DuplicateParty.vue";
import SymIcon from "./SymIcon.vue";

const props = defineProps<{
	data: PartyGraph,
	disabled: boolean
}>();

const env = injectEnv();
const duplicates = ref<PartyDuplicate[]>([]);
let checkingTimer = 0;
let waitingForDupQuery = false;
let nextDupQuery = false;

function check(delay = 500) {
	if (props.disabled) return;
	if (checkingTimer) clearTimeout(checkingTimer);
	checkingTimer = setTimeout(() => {
		checkingTimer = 0;
		queryServerForDuplicates();
	}, delay);
}

const appear = computed(() => duplicates.value.length > 0);

function queryServerForDuplicates() {
	if (waitingForDupQuery) {
		nextDupQuery = true;
	} else sendQueryForDuplicates();
}

const dupQuery = computed<DuplicityQuery>(() => {
	const dquery: DuplicityQuery = { exclude:props.data.party.id };
	if (Array.isArray(props.data.party.addresses)) {
		if (props.data.party.addresses.length > 0) {
			const ctr = props.data.party.addresses[0].country;
			if (typeof ctr === 'string' && ctr.length === 2) dquery.country = ctr;	// country of the checked record
		}
	}
	dquery.ico = props.data.party.ico;
	if (props.data.party.type === 'ORGANIZATION') dquery.company = props.data.party.company;
	if (Array.isArray(props.data.party.eaddresses) && props.data.party.eaddresses.length > 0) {
		// Need to access all eaddresses to trigger reactivity
		// The loop bellow doesn't do anything (but should be kept here)
		let access = 0;
		for (let ea of props.data.party.eaddresses) {
			if (ea.type || ea.value) access++;
		}
		dquery.eaddresses = props.data.party.eaddresses;
	} else dquery.eaddresses = []
	dquery.birthNumber = props.data.party.birthNumber;
	if (props.data.party.symbol) dquery.symbol = props.data.party.symbol;
	return dquery;
});

function processDuplicatesResponse(resp: DuplicityReponse) {
	duplicates.value.length = 0;
	if (Array.isArray(resp.duplicates) && resp.duplicates.length > 0) {
		for (let dup of resp.duplicates) duplicates.value.push(dup);
	}
}

function sendQueryForDuplicates() {
	waitingForDupQuery = true;
	env.net.fetch("findDuplicateParties.do", "POST", dupQuery.value)
		.then((resp) => {
			if (nextDupQuery) {
				nextDupQuery = false;
				sendQueryForDuplicates();	// again...current data are already obsolete
			} else {
				waitingForDupQuery = false;
				if (resp.ok) resp.json().then((data: DuplicityReponse) => processDuplicatesResponse(data))
			}
		})
		.catch((reason) => {
			console.error(reason);
			waitingForDupQuery = false;
			nextDupQuery = false;
		});
}

watch(() => dupQuery.value, (query, oldQuery) => {
	check();
});

const bus = injectEventBus();
watch(() => appear.value, (show) => {
	nextTick(() => {
		// Fire after UI update
		bus.fire("duplicates:appear", show);
	});
});

onMounted(() => {
	check();
})
</script>

<style>
.duplicates {
	display: inline-flex;
	flex-direction: column;
	background-color: var(--bg);
	color: var(--fg);
	border-radius: var(--panel-radius);
	overflow: hidden;
	box-shadow: var(--panel-shadow);
}

.duplicates-header {
	padding: 8px 16px;
	font-weight: 500;
	line-height: 18px;
	background-color: var(--bg);
	text-align: left;
	color: var(--fg);
	border-bottom: 1px solid var(--border-color);
}

.duplicates-items {
	padding: 16px;
	background-color: var(--bg);
	color: var(--fg);
	display: flex;
	flex-direction: column;
	row-gap: 8px;
}
</style>